import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

// TODO: использовать moment из src/helpers/dateTimeHelper.js
import moment from 'moment'
import 'moment-timezone'
import 'moment/dist/locale/ru'
import 'moment/dist/locale/kk'
import 'moment/dist/locale/uk'
import 'moment/dist/locale/tr'
import 'moment/dist/locale/uz'
import momentDurationFormatSetup from 'moment-duration-format'

import usePersonalCompanySettingsManager from '@/composables/managers/usePersonalCompanySettingsManager'

import { useCompanyStore } from '@/stores/companyStore'

import { PREDEFINED_RANGES_MAP } from '@/config/predefined-ranges'

export default function useMoment() {
	const { t: $t, locale } = useI18n()

	const companyStore = useCompanyStore()
	const {
		currentCompany,
	} = storeToRefs(companyStore)

	const {
		personalCompanySettingStartDay,
	} = usePersonalCompanySettingsManager()

	const momentFormats = {
		dateTimeWithoutTimezone: 'YYYY-MM-DDTHH:mm:ss',
		dateTimeWithTimezone: 'YYYY-MM-DDTHH:mm:ssZ',
	}

	const shortMonthsLabels = computed(() => {
		return [$t('Jan'), $t('Feb'), $t('Mar'), $t('Apr'), $t('May'), $t('Jun'), $t('Jul'), $t('Aug'), $t('Sep'), $t('Oct'), $t('Nov'), $t('Dec')]
	})

	momentDurationFormatSetup(moment)

	const companyTz = computed(() => {
		const companyTimeZone = currentCompany.value.company_tz
		return companyTimeZone
	})

	const guessedTz = computed(() => {
		const guessedTimeZone = moment.tz.guess()
		return guessedTimeZone
	})

	const tzShift = computed(() => {
		const now = moment.utc()
		const companyTzOffset = moment.tz.zone(companyTz.value).offset(now)
		const guessedTzOffset = moment.tz.zone(guessedTz.value).offset(now)

		return (companyTzOffset - guessedTzOffset) / 60
	})

	const getTimezoneUTC = (tz) => {
		let timezoneName = tz || currentCompany.value.company_tz
		return moment.tz(timezoneName).format('Z')
	}

	const predefinedRanges = computed(() => {
		const startTimeOfDay = personalCompanySettingStartDay.value
		return {
			today: {
				key: PREDEFINED_RANGES_MAP.TODAY,
				title: $t('Today'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			lastTwoDays: {
				key: PREDEFINED_RANGES_MAP.LAST_TWO_DAYS,
				title: $t('Last 2 days'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(1, 'days')
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'days')
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			yesterday: {
				key: PREDEFINED_RANGES_MAP.YESTERDAY,
				title: $t('Yesterday'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(1, 'days')
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.subtract(1, 'days')
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'days')
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'days')
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			thisWeek: {
				key: PREDEFINED_RANGES_MAP.THIS_WEEK,
				title: $t('This week'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.startOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.startOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			previousWeek: {
				key: PREDEFINED_RANGES_MAP.PREVIOUS_WEEK,
				title: $t('Previous week'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(1, 'week')
							.startOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.subtract(1, 'week')
							.endOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'week')
							.startOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'week')
							.endOf('week')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			lastSevenDays: {
				key: PREDEFINED_RANGES_MAP.LAST_SEVEN_DAYS,
				title: $t('Last 7 days'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(1, 'week')
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'week')
							.startOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('day')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			thisMonth: {
				key: PREDEFINED_RANGES_MAP.THIS_MONTH,
				title: $t('This month'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.startOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.startOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			previousMonth: {
				key: PREDEFINED_RANGES_MAP.PREVIOUS_MONTH,
				title: $t('Previous month'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(1, 'month')
							.startOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.subtract(1, 'month')
							.endOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'month')
							.startOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'month')
							.endOf('month')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			lastThirtyDays: {
				key: PREDEFINED_RANGES_MAP.LAST_THIRTY_DAYS,
				title: $t('Last 30 days'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(30, 'days')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(30, 'days')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			thisQuarter: {
				key: PREDEFINED_RANGES_MAP.THIS_QUARTER,
				title: $t('This quarter'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.startOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.startOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			previousQuarter: {
				key: PREDEFINED_RANGES_MAP.PREVIOUS_QUARTER,
				title: $t('Previous quarter'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(1, 'quarter')
							.startOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.subtract(1, 'quarter')
							.endOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'quarter')
							.startOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(1, 'quarter')
							.endOf('quarter')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			lastNinetyDays: {
				key: PREDEFINED_RANGES_MAP.LAST_NINETY_DAYS,
				title: $t('Last 90 days'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.subtract(90, 'days')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.subtract(90, 'days')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
			thisYear: {
				key: PREDEFINED_RANGES_MAP.THIS_YEAR,
				title: $t('This year'),
				getRange: () => {
					return [
						moment()
							.locale(locale.value)
							.startOf('year')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
						moment()
							.locale(locale.value)
							.endOf('year')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithoutTimezone),
					]
				},
				getRangeWithTz: () => {
					return [
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.startOf('year')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
						moment()
							.locale(locale.value)
							.tz(companyTz.value)
							.endOf('year')
							.add(startTimeOfDay, 'seconds')
							.format(momentFormats.dateTimeWithTimezone),
					]
				},
			},
		}
	})

	const predefinedRangesArray = computed(() => {
		return Object.values(predefinedRanges.value)
	})

	const predefinedRangeToDatePickerFormat = (predefinedRange) => {
		const range = predefinedRanges.value[predefinedRange]
		return range?.getRangeWithTz ? range?.getRangeWithTz() : range?.getRange()
	}

	const predefinedRangeToDataTransfer = (predefinedRange, fromKey = 'from', toKey = 'to') => {
		// console.log('predefinedRangeToDataTransfer tz', predefinedRange)
		if (typeof predefinedRange === 'object') {
			let [from, to] = predefinedRange
			// from = moment(from).tz(workedTz.value).format()
			// to = moment(to).tz(workedTz.value).format()
			return {
				[fromKey]: from,
				[toKey]: to,
			}
		} else {
			let [from, to] = predefinedRangeToDatePickerFormat(predefinedRange)
			return {
				[fromKey]: from,
				[toKey]: to,
			}
		}
	}

	/**
	 * Get current date
	 * @param {string} format moment format (set undefined for default format)
	 * @returns {string} formatted date string
	 */
	const getCurrentDate = (format = 'YYYY-MM-DD') => {
		return moment().locale(locale.value).format(format)
	}

	/**
	 * Get current time
	 * @param {string} format moment format (set undefined for default format)
	 * @param {boolean} useTimeZone use company timezone
	 * @returns {string} formatted date string
	 */
	const getCurrentTime = (format = 'YYYY-MM-DDTHH:mm:ss', useTimeZone) => {
		if (useTimeZone) {
			return moment().tz(companyTz.value).locale(locale.value).format(format)
		}
		return moment().locale(locale.value).format(format)
	}

	const getStartOfCompanyTzDate = () => {
		return moment().tz(companyTz.value).locale(locale.value).startOf('date').format()
	}

	/**
	 * @description Определяет дату и время начала рабочего дня. Например если старт дня в 8:00, а сегодня 9:00 - то вернет текущую дату в 08:00, а если текущее время меньше чем время начала рабочего дня - то вернет вчерашнюю дату со временем 08:00
	 * @returns {string} iso datetime
	 */
	const getDateTimeOfStartWorkDayIncludedCompanyTz = (startOfDate = 0) => {
		const now = moment().tz(companyTz.value).locale(locale.value)
		const startHour = Math.floor(startOfDate / 3600)
		const startMinute = Math.floor((startOfDate % 3600) / 60)
		const startOfToday = now.clone().hour(startHour).minute(startMinute).second(0)
		// debugger
		if (now.isBefore(startOfToday)) {
			return startOfToday.subtract(1, 'day').format()
		}
		return startOfToday.format()
	}

	const formatDate = (date, format = 'YYYY-MM-DD') => {
		if (!date) return null
		return moment(date).locale(locale.value).format(format)
	}

	/**
	 * Format date string
	 * @param {string} date date string
	 * @param {string} format moment format (set undefined for default format)
	 * @param {boolean} useTimeZone use company timezone
	 * @returns {string} formatted date string
	 */
	const formatDateTime = (date, format = 'YYYY-MM-DDTHH:mm:ss', useTimeZone) => {
		/* для вывода значения таймзоны в iso строке используйте формат 'YYYY-MM-DDTHH:mm:ssZ' */
		if (!date) return null
		if (useTimeZone) {
			return moment(date).tz(companyTz.value).locale(locale.value).format(format)
		}
		return moment(date).locale(locale.value).format(format)
	}

	/**
	 * Formats a date into a friendly string based on the given options.
	 * @param {Date} date - The date to format.
	 * @param {Object} [options={}] - The options for formatting.
	 * @param {boolean} [options.showDate=true] - Whether to hide the date.
	 * @param {boolean} [options.showTime=true] - Whether to hide the time.
	 * @param {boolean} [options.showSeconds=false] - Whether to show seconds.
	 * @param {string} [options.yearDisplay='always'] - How to display the year ('always', 'never', 'ifDiffers').
	 * @param {string} [options.yearFormat='short'] - How to display the month ('short', 'full').
	 * @param {string} [options.monthFormat='number'] - How to display the month ('number', 'shortName', 'fullName').
	 * @param {string} [options.dayMonthSeparator='.'] - How to separate date-month ('.', '/').
	 * @param {string} [options.dayYearSeparator='.'] - How to separate date-month to year ('.', '/').
	 * @param {string} [options.timePosition='start'] - Where to position the time ('start', 'end').
	 * @returns {string} - The formatted date and time string.
	 */
	const formatToFriendlyDateTime = (date, overriddenOptions = {}) => {
		if (!date) return null
		const options = Object.assign({
			showDate: true,
			showTime: true,
			showSeconds: false,
			yearDisplay: 'ifDiffers',
			yearFormat: 'short',
			monthFormat: 'number',
			dayMonthSeparator: '.',
			dayYearSeparator: '.',
			timePosition: 'start',
		}, overriddenOptions)

		let monthFormat = 'MM'
		if (options.monthFormat === 'shortName') {
			monthFormat = 'MMM'
		} else if (options.monthFormat === 'fullName') {
			monthFormat = 'MMMM'
		}

		let dateFormat = `DD${monthFormat === 'MM' ? options.dayMonthSeparator : ' '}${monthFormat}`
		let yearFormat = options.yearFormat === 'full' ? 'YYYY' : 'YY'
		let timeFormat = options.showSeconds ? 'HH:mm:ss' : 'HH:mm'

		if (options.yearDisplay === 'always') {
			dateFormat += `${monthFormat === 'MM' ? options.dayYearSeparator : ' '}${yearFormat}`
		} /* else if (options.yearDisplay === 'never') {
			dateFormat = `DD${options.dayMonthSeparator}MM`
		} */ else if (options.yearDisplay === 'ifDiffers') {
			const currentYear = new Date().getFullYear();
			const dateYear = moment(date).year()
			if (currentYear !== dateYear) {
				dateFormat += `${options.dayYearSeparator}${yearFormat}`
			}
		}

		let format = `${dateFormat}`
		if (options.showTime) {
			if (options.timePosition === 'start') {
				format = `${timeFormat} ${dateFormat}`
			} else {
				format = `${dateFormat} ${timeFormat}`
			}
		}
		return formatDateTime(date, format, true)
	}

	const formatToFriendlyPeriod = (period, overriddenOptions = {}) => {
		let isValidPeriod = false

		if (Array.isArray(period) && typeof period[0] === 'string' && typeof period[1] === 'string') {
			isValidPeriod = true
		}

		if (!isValidPeriod) return null

		const options = Object.assign({
			showDate: true,
			showTime: true,
			showSeconds: false,
			yearDisplay: 'ifDiffers',
			yearFormat: 'short',
			monthFormat: 'shortName',
			dayMonthSeparator: '.',
			dayYearSeparator: '.',
			timePosition: 'end',
		}, overriddenOptions)

		const dateFrom = formatDateTime(period[0], 'YYYY-MM-DD', true)
		const dateTo = formatDateTime(period[1], 'YYYY-MM-DD', true)
		if (dateFrom === dateTo) {
			const dateTimeFrom = formatToFriendlyDateTime(period[0], options)
			const timeTo = formatDateTime(period[1], 'HH:mm', true)

			return `${dateTimeFrom} - ${timeTo}`
		} else {
			const from = formatToFriendlyDateTime(period[0], options)
			const to = formatToFriendlyDateTime(period[1], options)

			return `${from} - ${to}`
		}

	}

	const formatPeriodToStringFromTo = (period, overriddenOptions = {}) => {
		let isValidPeriod = false

		if (Array.isArray(period) && typeof period[0] === 'string' && typeof period[1] === 'string') {
			isValidPeriod = true
		}

		if (!isValidPeriod) return null

		const options = Object.assign({
			showDate: true,
			showTime: true,
			showSeconds: false,
			// yearDisplay: 'ifDiffers',
			// yearFormat: 'short',
			// monthFormat: 'shortName',
			dayMonthSeparator: '.',
			dayYearSeparator: '.',
			timePosition: 'start',
		}, overriddenOptions)

		const from = formatToFriendlyDateTime(period[0], options)
		const to = formatToFriendlyDateTime(period[1], options)

		return $t('from [startDate] to [endDate]', {
			startDate: from,
			endDate: to,
		})
	}

	const formatIsoDateTimeWithCompanyTz = (date) => {
		return formatDateTime(date, 'YYYY-MM-DDTHH:mm:ssZ', true)
	}

	const getCurrentIsoDateTimeWithCompanyTz = () => {
		return formatIsoDateTimeWithCompanyTz(new Date())
	}

	const updateDateTimeString = (date, payload, format = 'YYYY-MM-DDTHH:mm:ss') => {
		return moment(date).set(payload).format(format)
	}

	const getDateWithoutTimezone = (date) => {
		return moment.parseZone(date).format('YYYY-MM-DDTHH:mm:ss')
	}

	const addTimezoneValue = (date, tz) => {
		const tzSuffix = moment(date).tz(tz).format('Z')
		return moment(date).format('YYYY-MM-DDTHH:mm:ss') + tzSuffix
	}

	const getFromNow = (date) => {
		return moment(date).locale(locale.value).fromNow(true)
	}

	const getCalendarTime = (date) => {
		return moment(date).locale(locale.value).calendar(null, {
			lastWeek: 'DD.MM.YYYY',
		})
	}

	const makeDurationFormatterFromSeconds = (seconds, overriddenOptions) => {
		if (seconds === null) return null

		const defaultOptions = {
			showDays: true,
			showSeconds: false,
		}
		const options = Object.assign(defaultOptions, overriddenOptions)
		let format = `hh [${$t('h')}] mm [${$t('min')}]`
		if (options.showDays) {
			format = `d [${$t('d')}] ` + format
		}
		if (options.showSeconds) {
			format = format + ` ss [${$t('s')}]`
		}
		const formatTime = moment.duration(seconds, 'seconds').format(format)

		return formatTime
	}

	const makeDurationFormatterFromTime = (time) => {
		const format = `d [${$t('d')}] hh [${$t('h')}] mm [${$t('min')}]`
		const formatTime = moment.duration(time).format(format)

		return formatTime
	}

	const countDifferenceDurationBetweenTwoDates = (period, format = `d [${$t('d')}] hh [${$t('h')}] mm [${$t('min')}]`) => {
		const [startDate, endDate] = period
		if (startDate && endDate) {
			const formatDuration = moment.duration(moment(endDate).diff(moment(startDate))).format(format)

			return formatDuration
		}
	} // нуждается в проверке

	const calculateSumSecondsOfDurations = (durations = []) => {
		const sum = durations.reduce((total, currentValue) => {
			const isSeconds = typeof currentValue === 'number'
			return total.add(isSeconds ? currentValue * 1000 : currentValue)
		}, moment.duration(0))
		return sum.asSeconds()
	}

	const calculateAverageDurationAsSeconds = (durations = []) => {
		const sum = calculateSumSecondsOfDurations(durations)
		const count = durations.length
		return parseInt(sum / count)
	}

	/**
	 * Вычисляет длительность из периода в секундах.
	 * @param {Array<string>} period - Массив из двух строк ISO datetime [start, end].
	 * @returns {number} - Длительность в секундах.
	 */
	const calculateDurationAsSecondsFromPeriod = (period) => {
		if (!Array.isArray(period) || period.length !== 2) {
			throw new Error("Invalid period format. Expected an array with two ISO datetime strings.")
		}

		const [start, end] = period.map((datetime) => moment(datetime, moment.ISO_8601))

		if (!start.isValid() || !end.isValid()) {
			throw new Error("Invalid date in period. Ensure both dates are valid ISO strings.")
		}

		const durationInSeconds = Math.abs(end.diff(start, 'seconds'))

		return durationInSeconds
	}

	/**
	 *
	 * @param {array} dateRange
	 * @param {object} overriddenParams
	 * @param {string} overriddenParams.timeFormat moment format of time, default 'HH:mm'
	 * @param {string} overriddenParams.dateFormat moment format of date, default 'DD.MM.YY'
	 * @param {boolean} overriddenParams.useTimeZone use or not timezone
	 * @param {string} overriddenParams.firstInOrder first in order to formate date range. Enum['timeFormat', 'dateFormat']
	 *
	 * @returns {string} formatted date range string
	 */

	const formatDateRange = (dateRange, overriddenParams) => {
		if (!dateRange?.length) return null

		const defaultParams = {
			timeFormat: 'HH:mm',
			dateFormat: 'DD.MM.YY',
			useTimeZone: true,
			firstInOrder: 'timeFormat',
		}
		const params = Object.assign(defaultParams, overriddenParams)

		const secondInOrder = params.firstInOrder === 'timeFormat' ? 'dateFormat' : 'timeFormat'

		const format = `${params[params.firstInOrder]} ${params[secondInOrder]}` //Для периода с разными датами или отсутсвием одной даты

		if (dateRange?.[0] && dateRange?.[1]) {

			//если дата одинаковая
			if ( new Date(dateRange[0]).getDate() === new Date(dateRange[1]).getDate() && new Date(dateRange[0]).getMonth() === new Date(dateRange[1]).getMonth() && new Date(dateRange[0]).getFullYear() === new Date(dateRange[1]).getFullYear() ) {
				if (params.firstInOrder === 'timeFormat') {

					const fromTime = formatDateTime(dateRange[0], params.timeFormat || 'HH:mm', params.useTimeZone)

					const toDateTime = formatDateTime(dateRange[1], `${params.timeFormat || 'HH:mm'} ${params.dateFormat}`, params.useTimeZone)

					return `${fromTime} - ${toDateTime}`
				}
			} else {
				const from = formatDateTime(dateRange[0], format, params.useTimeZone)

				const to = formatDateTime(dateRange[1], format, params.useTimeZone)

				return `${from} - ${to}`
			}

		} else {
			return formatDateTime(dateRange[0], format, params.useTimeZone)
		}
	}

	const formatDuration = (duration) => {
		return moment.duration(duration)
	}

	const formatDurationInSeconds = (duration) => {
		return moment.duration(duration).asSeconds()
	}

	const isSameDateToday = (date) => {
		const currentDate = moment(date).tz(companyTz.value).format()

		const today = moment().tz(companyTz.value).format()

		return moment(currentDate).isSame(today, 'day')
	}

	const formatTimeToSecondsAsNumber = (time) => {
		const [hours, minutes] = time.split(':').map(Number)

		const secondsAsNumber = (hours * 60 + minutes) * 60

		return secondsAsNumber.toFixed(2)
	}

	const formatHoursAsNumberToTime = (hours) => {
		const time = moment.duration(+hours * 3600, 'seconds').format('HH:mm')

		return hours > 0 ? time : '00:00'
	}

	const formatSecondsToDuration = (seconds) => {
		const duration = moment.duration(seconds, 'seconds');
		const hours = Math.floor(duration.asHours());
		const minutes = duration.minutes();
		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

	const isBetweenPeriod = (date, period) => {
		const currentTime = getCurrentTime('YYYY-MM-DDTHH:mm:ssZ', true)
		return moment(date ?? currentTime).isBetween(period[0], period[1])
	}

	/**
	 * Checks if two time periods overlap using Moment.js.
	 * @param {Array<string>} period1 - The first period as an array of two ISO datetime strings [start, end].
	 * @param {Array<string>} period2 - The second period as an array of two ISO datetime strings [start, end].
	 * @returns {boolean} - True if the periods overlap, false otherwise.
	 */
	const isPeriodsOverlap = (period1, period2) => {
		const [start1, end1] = period1.map(date => moment(date));
		const [start2, end2] = period2.map(date => moment(date));

		return start1.isSameOrBefore(end2) && start2.isSameOrBefore(end1);
	}

	/**
	 * Определяет пограничный период (самая ранняя start и самая поздняя end) из массива периодов.
	 * @param {Array<Array<string>>} periods - Массив периодов, каждый период представлен как [start, end].
	 * @returns {Array<string>} - Массив с самой ранней start и самой поздней end датой [earliestStart, latestEnd].
	 */
	const getBoundaryPeriod = (periods) => {
		if (!periods || periods.length === 0) return null

		// Преобразуем строки ISO в объекты Moment
		let earliestStart = moment(periods[0][0])
		let latestEnd = moment(periods[0][1])

		periods.forEach(([start, end]) => {
			const startMoment = moment(start)
			const endMoment = moment(end)

			if (startMoment.isBefore(earliestStart)) {
				earliestStart = startMoment
			}

			if (endMoment.isAfter(latestEnd)) {
				latestEnd = endMoment
			}
		})

		const format = 'YYYY-MM-DDTHH:mm:ssZ'
		return [formatDateTime(earliestStart, format, true), formatDateTime(latestEnd, format, true)]
	}

	/**
	 * Определяет минимальную и максимальную дату из массива дат.
	 * @param {Array<string>} dates - Массив дат в формате 'YYYY-MM-DD'.
	 * @returns {Array<string>} - Массив с минимальной и максимальной датой [minDate, maxDate].
	 */
	const getMinMaxDates = (dates) => {
		if (!dates || dates.length === 0) return null

		const minDate = dates.reduce((min, date) => (date < min ? date : min), dates[0])
		const maxDate = dates.reduce((max, date) => (date > max ? date : max), dates[0])

		return [minDate, maxDate]
	}

	/**
	 * Получить массив дат в формате 'YYYY-MM-DD' между двумя датами.
	 * @param {Array<string>} period - Массив из двух строк ISO datetime [start, end].
	 * @returns {Array<string>} - Массив дат в формате 'YYYY-MM-DD'.
	 * @example
	 * getArrayOfDates(['2022-01-01T00:00:00Z', '2022-01-03T00:00:00Z'])
	 * // ['2022-01-01', '2022-01-02', '2022-01-03']
	 * @example
	 * getArrayOfDates(['2022-01-01T00:00:00Z', '2022-01-01T00:00:00Z'])
	 * // ['2022-01-01']
	 */
	const getArrayOfDates = (period) => {
		const [startDate, endDate] = period
		const dates = []
		let currentDate = moment(startDate).startOf('day')
		const lastDate = moment(endDate).startOf('day')
		while (currentDate <= lastDate) {
			dates.push(currentDate.format('YYYY-MM-DD'))
			currentDate = currentDate.add(1, 'days')
		}
		return dates
	}

	/**
	 * Фильтрует и обрезает массив периодов по заданному периоду
	 * @param {string[][]} periods - Массив периодов, каждый период - массив из двух ISO строк
	 * @param {string[]} filterPeriod - Период фильтрации, массив из двух ISO строк
	 * @returns {string[][]} Отфильтрованный и обрезанный массив периодов
	 */
	function filterAndTrimPeriods(periods, filterPeriod) {
		const [filterStart, filterEnd] = filterPeriod.map(date => moment(date))

		return periods
			.map(([start, end]) => {
				const periodStart = moment(start)
				const periodEnd = moment(end)

				if (periodEnd.isBefore(filterStart) || periodStart.isAfter(filterEnd)) {
					return null // Полностью вне диапазона
				}

				const newStart = moment.max(periodStart, filterStart)
				const newEnd = moment.min(periodEnd, filterEnd)

				return [newStart.toISOString(), newEnd.toISOString()]
			})
			.filter(Boolean)
	}

	/**
	 * @description Возвращает значение продолжительности в секундах из десятичного значения часов
	 * @param {number} hours - Десятичное значение часов
	 * @returns {number} - Продолжительность в секундах
	 */
	const getDurationInSecondsFromDecimalHours = (hours) => {
		if (hours === null) return null

		const duration = moment.duration(hours, 'hours')
		const seconds = duration.asSeconds()

		return seconds
	}

	/**
	 * @description Возвращает значение десятичных часов из продолжительности в секундах
	 * @param {number} seconds - Продолжительность в секундах
	 * @returns {number} - Десятичное значение часов
	 */
	const getDecimalHoursFromDurationInSeconds = (seconds) => {
		if (seconds === null) return null

		const duration = moment.duration(seconds, 'seconds')
		const hours = duration.asHours()

		return hours
	}

	return {
		moment,

		shortMonthsLabels,

		companyTz,
		guessedTz,
		tzShift,
		getTimezoneUTC,

		predefinedRanges,
		predefinedRangesArray,
		predefinedRangeToDataTransfer,

		getCurrentDate,
		getCurrentTime,
		getStartOfCompanyTzDate,
		getDateTimeOfStartWorkDayIncludedCompanyTz,

		formatDate,
		formatDateTime,
		formatToFriendlyDateTime,
		formatToFriendlyPeriod,
		formatPeriodToStringFromTo,

		updateDateTimeString,

		getDateWithoutTimezone,
		addTimezoneValue,

		getFromNow,
		getCalendarTime,
		makeDurationFormatterFromSeconds,
		makeDurationFormatterFromTime,
		countDifferenceDurationBetweenTwoDates,
		calculateSumSecondsOfDurations,
		calculateAverageDurationAsSeconds,
		calculateDurationAsSecondsFromPeriod,
		formatDateRange,
		formatDuration,
		formatDurationInSeconds,
		formatIsoDateTimeWithCompanyTz,
		getCurrentIsoDateTimeWithCompanyTz,
		isSameDateToday,
		predefinedRangeToDatePickerFormat,
		formatTimeToSecondsAsNumber,
		formatHoursAsNumberToTime,
		formatSecondsToDuration,

		isBetweenPeriod,
		isPeriodsOverlap,
		getBoundaryPeriod,
		getMinMaxDates,
		getArrayOfDates,
		filterAndTrimPeriods,

		getDurationInSecondsFromDecimalHours,
		getDecimalHoursFromDurationInSeconds,
	}
}
